import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

function Toast({ id, type = 'info', title, message, onClose }) {
  const tone = icons[type] ? type : 'info';
  const Icon = icons[tone];
  // Multi-line toasts (title + message) need icon pinned to the first line
  // so it reads like a conventional notification. Single-line toasts look
  // better with everything centered — icon, text, and close button were
  // landing 1–2 px apart because their intrinsic heights differ.
  const hasSubtitle = Boolean(title && message);
  const align = hasSubtitle ? 'align-start' : 'align-center';
  // When we're pinned to the top of a multi-line toast, a 2 px nudge down
  // keeps the icon visually sitting on the baseline of the title.
  const iconOffset = hasSubtitle ? ' offset' : '';

  return (
    <div
      className={`toast ${tone} ${align}`}
      role="alert"
    >
      <Icon className={`toast-icon${iconOffset}`} />
      <div className="toast-content">
        {title && <p className="toast-title">{title}</p>}
        {message && <p className="toast-message">{message}</p>}
      </div>
      <button
        onClick={() => onClose(id)}
        className="toast-dismiss"
        aria-label="Dismiss notification"
      >
        <X />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // showToast(message, type) - simple API
  const showToast = useCallback((message, type = 'info') => {
    return addToast({ type, title: message });
  }, [addToast]);

  const success = useCallback((title, message) => addToast({ type: 'success', title, message }), [addToast]);
  const error = useCallback((title, message) => addToast({ type: 'error', title, message }), [addToast]);
  const info = useCallback((title, message) => addToast({ type: 'info', title, message }), [addToast]);
  const warning = useCallback((title, message) => addToast({ type: 'warning', title, message }), [addToast]);

  const contextValue = useMemo(() => ({
    showToast,
    addToast,
    success,
    error,
    info,
    warning,
  }), [showToast, addToast, success, error, info, warning]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toast-stack">
        {toasts.map(t => (
          <div key={t.id} className="toast-frame">
            <Toast {...t} onClose={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
